import { Fragment, useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import {
  XMarkIcon,
  PencilIcon,
  EyeIcon,
  ArrowPathIcon,
  CodeBracketIcon,
  CubeIcon,
  SparklesIcon,
  PaperAirplaneIcon,
  UserIcon,
  LightBulbIcon,
  FunnelIcon,
  ChevronDownIcon,
  AdjustmentsHorizontalIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  ChartPieIcon,
  ArrowTrendingUpIcon,
  CursorArrowRaysIcon,
  Squares2X2Icon,
  GlobeAltIcon,
  CircleStackIcon,
  TableCellsIcon,
  SwatchIcon,
} from '@heroicons/react/24/outline';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiHelpers } from '../../services/api';
import { useDashboardStore } from '../../store/dashboardStore';
import { useAuthStore } from '../../store/authStore';
import { useCreditCosts } from '../../hooks/useCreditCosts';
import toast from 'react-hot-toast';
import VisualChartBuilder from '../charts/VisualChartBuilder';
import ThinkingChain from '../common/ThinkingChain';
import { formatCellValue, getPreviewOption } from '../../utils/chartPreviewUtils';
import FilterConfigurator from '../chart-builder/FilterConfigurator';
import TableColorConfigurator from '../chart-builder/TableColorConfigurator';
import { getTableCellStyle, getTableHeaderStyle } from '../../utils/tableColorUtils';

/**
 * Parse an existing SQL query into visual config format
 * Returns { dimensions, measures, filters, sortBy, limit } or null if parsing fails
 */
function parseSQLToVisualConfig(sql) {
  if (!sql || typeof sql !== 'string') return null;
  
  try {
    // Normalize whitespace
    const normalizedSQL = sql.replace(/\s+/g, ' ').trim();
    
    // Check if it's a simple enough query to parse (no CTEs, UNION, subqueries, etc.)
    if (/\bWITH\b/i.test(normalizedSQL) || /\bUNION\b/i.test(normalizedSQL)) {
      console.log('[parseSQLToVisualConfig] Complex query with CTEs or UNION, skipping parse');
      return null;
    }
    
    // Check for subqueries - FROM followed by ( indicates a subquery
    if (/\bFROM\s*\(/i.test(normalizedSQL)) {
      console.log('[parseSQLToVisualConfig] Complex query with subquery, skipping parse');
      return null;
    }
    
    // Check for nested SELECT statements (more than one SELECT keyword)
    const selectCount = (normalizedSQL.match(/\bSELECT\b/gi) || []).length;
    if (selectCount > 1) {
      console.log('[parseSQLToVisualConfig] Complex query with multiple SELECTs, skipping parse');
      return null;
    }
    
    const dimensions = [];
    const measures = [];
    const filters = [];
    const sortBy = [];
    let limit = 100;
    
    // Extract SELECT clause
    const selectMatch = normalizedSQL.match(/SELECT\s+([\s\S]*?)\s+FROM/i);
    if (!selectMatch) return null;
    
    const selectClause = selectMatch[1].trim();
    
    // Handle SELECT *
    if (selectClause === '*') {
      return { dimensions: [], measures: [], filters: [], sortBy: [], limit: 100 };
    }
    
    // Split select clause by comma (handling nested parentheses)
    const selectParts = [];
    let current = '';
    let parenDepth = 0;
    for (let i = 0; i < selectClause.length; i++) {
      const char = selectClause[i];
      if (char === '(') parenDepth++;
      else if (char === ')') parenDepth--;
      else if (char === ',' && parenDepth === 0) {
        if (current.trim()) selectParts.push(current.trim());
        current = '';
        continue;
      }
      current += char;
    }
    if (current.trim()) selectParts.push(current.trim());
    
    // Extract GROUP BY columns to identify dimensions
    const groupByMatch = normalizedSQL.match(/GROUP\s+BY\s+([\s\S]*?)(?:HAVING|ORDER|LIMIT|$)/i);
    const groupByColumns = [];
    if (groupByMatch) {
      const groupByClause = groupByMatch[1].trim();
      // Split by comma (simple split for GROUP BY)
      groupByClause.split(',').forEach(col => {
        const cleaned = col.trim().replace(/[`'"]/g, '');
        if (cleaned) groupByColumns.push(cleaned.toLowerCase());
      });
    }
    
    // Parse each select part
    selectParts.forEach((part, index) => {
      // Check for aggregation functions
      const aggMatch = part.match(/^(COUNT|SUM|AVG|MIN|MAX)\s*\(\s*(DISTINCT\s+)?(.+?)\s*\)\s*(?:AS\s+)?([`'\w]*)?$/i);
      
      if (aggMatch) {
        const aggFunc = aggMatch[1].toUpperCase();
        const isDistinct = !!aggMatch[2];
        const columnExpr = aggMatch[3].trim();
        const alias = aggMatch[4]?.replace(/[`'"]/g, '') || `${columnExpr}_${aggFunc.toLowerCase()}`;
        
        // Extract just the column name from expression (handle table.column)
        const columnName = columnExpr.replace(/.*\./, '').replace(/[`'"]/g, '');
        
        measures.push({
          id: `measure_${index}`,
          column: columnName,
          aggregation: isDistinct ? 'COUNT_DISTINCT' : aggFunc,
          alias: alias
        });
      } else {
        // Not an aggregation - check if it's in GROUP BY (dimension) or raw column
        // Extract the alias if present
        const asMatch = part.match(/\s+AS\s+([`'"\w]+)\s*$/i);
        let alias = '';
        let expression = part;
        
        if (asMatch) {
          alias = asMatch[1].replace(/[`'"]/g, '');
          expression = part.replace(/\s+AS\s+[`'"\w]+\s*$/i, '').trim();
        }
        
        // Extract base column name
        const columnMatch = expression.match(/(?:.*\.)?([`'"\w]+)$/);
        const columnName = columnMatch ? columnMatch[1].replace(/[`'"]/g, '') : expression;
        
        // Determine the final name (alias or column)
        const finalName = alias || columnName;
        
        // Check if this is a dimension (in GROUP BY)
        const isGroupBy = groupByColumns.includes(finalName.toLowerCase()) || 
                          groupByColumns.includes(columnName.toLowerCase());
        
        if (isGroupBy || groupByColumns.length === 0) {
          // It's a dimension or there's no GROUP BY (simple select)
          dimensions.push({
            id: `dim_${index}`,
            column: expression, // Keep the full expression for complex cases
            alias: alias || undefined
          });
        } else {
          // Not in GROUP BY and not aggregated - treat as measure with no aggregation
          measures.push({
            id: `measure_${index}`,
            column: columnName,
            aggregation: 'none',
            alias: alias || undefined
          });
        }
      }
    });
    
    // Extract ORDER BY
    const orderByMatch = normalizedSQL.match(/ORDER\s+BY\s+([\s\S]*?)(?:LIMIT|$)/i);
    if (orderByMatch) {
      const orderByClause = orderByMatch[1].trim();
      orderByClause.split(',').forEach((part, index) => {
        const cleaned = part.trim();
        const dirMatch = cleaned.match(/\s+(ASC|DESC)\s*$/i);
        const direction = dirMatch ? dirMatch[1].toUpperCase() : 'ASC';
        const column = cleaned.replace(/\s+(ASC|DESC)\s*$/i, '').trim().replace(/[`'"]/g, '');
        
        if (column) {
          sortBy.push({
            id: `sort_${index}`,
            column: column,
            direction: direction
          });
        }
      });
    }
    
    // Extract LIMIT
    const limitMatch = normalizedSQL.match(/LIMIT\s+(\d+)/i);
    if (limitMatch) {
      limit = parseInt(limitMatch[1], 10);
    }
    
    // Extract WHERE conditions (basic parsing)
    const whereMatch = normalizedSQL.match(/WHERE\s+([\s\S]*?)(?:GROUP\s+BY|ORDER\s+BY|LIMIT|$)/i);
    if (whereMatch) {
      const whereClause = whereMatch[1].trim();
      // Split by AND (simplified - doesn't handle OR or nested conditions well)
      whereClause.split(/\s+AND\s+/i).forEach((condition, index) => {
        const cleaned = condition.trim();
        
        // Try to parse common operators
        const opMatch = cleaned.match(/^(.+?)\s*(=|!=|<>|>=|<=|>|<|LIKE|NOT LIKE|IS NULL|IS NOT NULL|IN)\s*(.*)$/i);
        
        if (opMatch) {
          const column = opMatch[1].trim().replace(/[`'"]/g, '');
          let operator = opMatch[2].toUpperCase();
          let value = opMatch[3]?.trim().replace(/^['"]|['"]$/g, '') || '';
          
          // Normalize operators
          if (operator === '<>') operator = '!=';
          
          // Don't add IS NULL/IS NOT NULL values
          if (operator === 'IS NULL' || operator === 'IS NOT NULL') {
            value = '';
          }
          
          filters.push({
            id: `filter_${index}`,
            column: column,
            operator: operator,
            value: value
          });
        }
      });
    }
    
    console.log('[parseSQLToVisualConfig] Parsed config:', { dimensions, measures, filters, sortBy, limit });
    return { dimensions, measures, filters, sortBy, limit };
  } catch (e) {
    console.error('[parseSQLToVisualConfig] Error parsing SQL:', e);
    return null;
  }
}

let geoMapRegistered = false;

const registerGeoMap = async () => {
  if (geoMapRegistered) return true;
  try {
    const response = await fetch('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json');
    const topology = await response.json();
    const topojson = await import('topojson-client');
    const geojson = topojson.feature(topology, topology.objects.countries);
    echarts.registerMap('world', geojson);
    geoMapRegistered = true;
    return true;
  } catch (error) {
    console.error('[EditChartModal] Failed to load world map:', error);
    return false;
  }
};

const CHART_TYPES = [
  { id: 'bar', name: 'Bar', icon: ChartBarIcon, description: 'Compare categories' },
  { id: 'line', name: 'Line', icon: PresentationChartLineIcon, description: 'Show trends' },
  { id: 'pie', name: 'Pie', icon: ChartPieIcon, description: 'Show proportions' },
  { id: 'donut', name: 'Donut', icon: ChartPieIcon, description: 'Ring proportions' },
  { id: 'area', name: 'Area', icon: ArrowTrendingUpIcon, description: 'Volume over time' },
  { id: 'scatter', name: 'Scatter', icon: CursorArrowRaysIcon, description: 'Data correlation' },
  { id: 'funnel', name: 'Funnel', icon: ArrowTrendingUpIcon, description: 'Conversion stages' },
  { id: 'heatmap', name: 'Heatmap', icon: Squares2X2Icon, description: 'Density patterns' },
  { id: 'cohort_table', name: 'Cohort', icon: Squares2X2Icon, description: 'Retention matrix' },
  { id: 'geo', name: 'Geo Map', icon: GlobeAltIcon, description: 'Location mapping' },
  { id: 'table', name: 'Table', icon: TableCellsIcon, description: 'Detailed data' },
  { id: 'kpi', name: 'KPI', icon: CircleStackIcon, description: 'Single metric' },
];

export default function EditChartModal({ isOpen, onClose, chart, onChartUpdated }) {
  // Debug: Log props on every render
  console.log('[EditChartModal] Render - isOpen:', isOpen, 'chart:', chart);
  
  const [chartName, setChartName] = useState('');
  const [chartType, setChartType] = useState('');
  const [query, setQuery] = useState('');
  const [description, setDescription] = useState('');
  const [xAxisLabel, setXAxisLabel] = useState('');
  const [yAxisLabel, setYAxisLabel] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('query');
  const [mobileTab, setMobileTab] = useState('preview');
  const [showSettings, setShowSettings] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewOption, setPreviewOption] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  
  // Visual builder state
  const [mode, setMode] = useState('sql'); // 'visual' or 'sql'
  const [schema, setSchema] = useState(null);
  const [sourceType, setSourceType] = useState('datasource');
  const [tableName, setTableName] = useState('');
  const [visualConfig, setVisualConfig] = useState(null);
  
  // AI Assistant state
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const aiMessagesEndRef = useRef(null);
  const [aiProvider, setAiProvider] = useState('anthropic');
  const [aiModel, setAiModel] = useState('');
  // Thinking chain (streaming progress)
  const [aiThinkingStep, setAiThinkingStep] = useState(0);
  const [aiThinkingStartTime, setAiThinkingStartTime] = useState(null);
  const [aiElapsedTime, setAiElapsedTime] = useState(0);
  const [aiStepDetails, setAiStepDetails] = useState({});
  
  // Geo map state
  const [mapLoaded, setMapLoaded] = useState(geoMapRegistered);

  // Dynamic filter configuration state
  const [hideLegend, setHideLegend] = useState(false);
  const [multiSeriesLayout, setMultiSeriesLayout] = useState('auto');
  const [filterConfig, setFilterConfig] = useState([]);
  const [tableColorRules, setTableColorRules] = useState([]);
  const [showTableStyling, setShowTableStyling] = useState(false);
  
  const { updateChart } = useDashboardStore();
  const { user, getCurrentCompanyId } = useAuthStore();
  const { formatCost } = useCreditCosts();
  
  // Fetch available AI models
  const { data: modelsData } = useQuery({
    queryKey: ['aiModels'],
    queryFn: () => apiHelpers.getModels(),
    staleTime: 5 * 60 * 1000
  });
  
  const aiModels = modelsData?.data?.models || [];
  const aiProviders = modelsData?.data?.providers || {};
  
  // Set default model when provider changes or models load
  useEffect(() => {
    if (modelsData?.data?.providers?.[aiProvider]) {
      setAiModel(modelsData.data.providers[aiProvider].defaultModel);
    }
  }, [aiProvider, modelsData]);
  
  // Load world map for geo chart previews
  useEffect(() => {
    if (chartType === 'geo' && !mapLoaded) {
      registerGeoMap().then(setMapLoaded);
    }
  }, [chartType, mapLoaded]);

  const runAutoPreview = async (chartData) => {
    setIsPreviewLoading(true);
    setPreviewError(null);
    try {
      const response = await apiHelpers.previewChartQuery({
        chartId: chartData._id,
        query: chartData.query,
        type: chartData.type || 'bar',
      });
      const rows = response.data.data;
      setPreviewData(rows);
      let opt = getPreviewOption(chartData.type || 'bar', rows || [], { config: { ...chartData.chartConfig, multiSeriesLayout: chartData.chartConfig?.multiSeriesLayout || 'auto' } }, { mapLoaded });
      if (opt) {
        const axesTypes = ['pie', 'donut', 'table', 'kpi', 'cohort_table', 'geo', 'funnel'];
        const xLbl = chartData.chartConfig?.xAxis?.label;
        const yLbl = chartData.chartConfig?.yAxis?.label;
        if (!axesTypes.includes(chartData.type)) {
          if (xLbl && opt.xAxis && !Array.isArray(opt.xAxis)) {
            opt.xAxis = { ...opt.xAxis, name: xLbl, nameLocation: 'middle', nameGap: 30, nameTextStyle: { fontWeight: 'bold', fontSize: 12 } };
          }
          if (yLbl && opt.yAxis && !Array.isArray(opt.yAxis)) {
            opt.yAxis = { ...opt.yAxis, name: yLbl, nameLocation: 'middle', nameGap: 50, nameRotate: 90, nameTextStyle: { fontWeight: 'bold', fontSize: 12 } };
          }
        }
        if (chartData.chartConfig?.legend?.show === false) {
          opt.legend = undefined;
        }
      }
      setPreviewOption(opt || response.data.echartsOption);
    } catch (error) {
      console.error('Auto preview error:', error);
      setPreviewError(error.response?.data?.error || 'Failed to load chart preview');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // Initialize form with chart data
  useEffect(() => {
    console.log('[EditChartModal] useEffect - isOpen:', isOpen, 'chart:', chart?.name);
    if (chart && isOpen) {
      console.log('[EditChartModal] Initializing form with chart data');
      setChartName(chart.name || '');
      setChartType(chart.type || 'bar');
      setQuery(chart.query || '');
      setDescription(chart.description || '');
      setXAxisLabel(chart.chartConfig?.xAxis?.label || '');
      setYAxisLabel(chart.chartConfig?.yAxis?.label || '');
      setHideLegend(chart.chartConfig?.legend?.show === false);
      setMultiSeriesLayout(chart.chartConfig?.multiSeriesLayout || 'auto');
      setPreviewData(null);
      setPreviewOption(null);
      setPreviewError(null);
      setActiveTab('query');
      setShowSettings(true);
      setShowFilters(false);
      setMode('sql');
      
      if (chart.query) {
        const parsedConfig = parseSQLToVisualConfig(chart.query);
        if (parsedConfig) {
          setVisualConfig(parsedConfig);
        } else {
          setVisualConfig({ isComplexQuery: true, dimensions: [], measures: [], filters: [], sortBy: [], limit: 100 });
        }
      } else {
        setVisualConfig(null);
      }
      
      setAiMessages([]);
      setAiInput('');
      setAiSuggestion(null);
      setFilterConfig(chart.filterConfig || []);
      setTableColorRules(chart.chartConfig?.tableConfig?.colorRules || []);
      setShowTableStyling(false);
      fetchSchemaForChart();

      if (chart.query) {
        runAutoPreview(chart);
      }
    }
  }, [chart, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const hasAxes = !['pie', 'donut', 'table', 'kpi', 'cohort_table', 'geo', 'funnel'].includes(chartType);

  useEffect(() => {
    if (previewData && previewData.length > 0) {
      const cfgWithLayout = { config: { ...chart?.chartConfig, multiSeriesLayout } };
      let newOption = getPreviewOption(chartType, previewData, cfgWithLayout, { mapLoaded });
      if (newOption) {
        const axesTypes = ['pie', 'donut', 'table', 'kpi', 'cohort_table', 'geo', 'funnel'];
        if (!axesTypes.includes(chartType)) {
          if (xAxisLabel.trim() && newOption.xAxis && !Array.isArray(newOption.xAxis)) {
            newOption.xAxis = { ...newOption.xAxis, name: xAxisLabel.trim(), nameLocation: 'middle', nameGap: 30, nameTextStyle: { fontWeight: 'bold', fontSize: 12 } };
          }
          if (yAxisLabel.trim() && newOption.yAxis && !Array.isArray(newOption.yAxis)) {
            newOption.yAxis = { ...newOption.yAxis, name: yAxisLabel.trim(), nameLocation: 'middle', nameGap: 50, nameRotate: 90, nameTextStyle: { fontWeight: 'bold', fontSize: 12 } };
          }
        }
        if (hideLegend) {
          newOption.legend = undefined;
        }
        setPreviewOption(newOption);
      }
    }
  }, [chartType, xAxisLabel, yAxisLabel, hideLegend, multiSeriesLayout, mapLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll AI messages
  useEffect(() => {
    aiMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages]);

  // Elapsed time while AI is thinking
  useEffect(() => {
    if (!aiThinkingStartTime) return;
    const interval = setInterval(() => {
      setAiElapsedTime(Date.now() - aiThinkingStartTime);
    }, 100);
    return () => clearInterval(interval);
  }, [aiThinkingStartTime]);

  // Fetch schema for the chart's data source
  const fetchSchemaForChart = async () => {
    if (!chart) return;
    
    // Get companyId from chart or fallback to auth store
    let companyId = chart.companyId?._id || chart.companyId;
    if (!companyId) {
      // Fallback: try to get from auth store
      const authState = useAuthStore.getState();
      companyId = authState.getCurrentCompanyId();
    }
    if (!companyId) {
      console.warn('[EditChartModal] No companyId found');
      return;
    }
    
    try {
      const response = await apiHelpers.getChartSources({ companyId });
      const sources = response.data;
      
      console.log('[EditChartModal] Chart info:', {
        isMart: chart.isMart,
        martId: chart.martId,
        isWideView: chart.isWideView,
        wideViewName: chart.wideViewName,
        dataSourceId: chart.dataSourceId
      });
      console.log('[EditChartModal] Available sources:', {
        dataSources: sources.dataSources?.length,
        wideViews: sources.wideViews?.length,
        marts: sources.marts?.length
      });
      
      let sourceData = null;
      let type = 'datasource';
      let name = '';
      
      // Find the matching source
      if (chart.isMart && chart.martId) {
        const martId = (typeof chart.martId === 'object' ? chart.martId._id : chart.martId)?.toString();
        sourceData = sources.marts?.find(m => m.id?.toString() === martId);
        type = 'mart';
        name = sourceData?.tableName || sourceData?.dbtModelName || '';
        console.log('[EditChartModal] Looking for mart:', martId, 'Found:', !!sourceData);
      } else if (chart.isWideView && chart.wideViewName) {
        sourceData = sources.wideViews?.find(w => w.name === chart.wideViewName);
        type = 'wideview';
        name = sourceData?.tableName || sourceData?.dbtModelName || '';
        console.log('[EditChartModal] Looking for wideView:', chart.wideViewName, 'Found:', !!sourceData);
      } else if (chart.dataSourceId) {
        const dsId = (typeof chart.dataSourceId === 'object' ? chart.dataSourceId._id : chart.dataSourceId)?.toString();
        console.log('[EditChartModal] Available dataSource IDs:', sources.dataSources?.map(d => d.id?.toString()));
        sourceData = sources.dataSources?.find(d => d.id?.toString() === dsId);
        type = 'datasource';
        
        // If not found in ready sources but we have the dataSourceId object with details, use those
        if (!sourceData && typeof chart.dataSourceId === 'object' && chart.dataSourceId.bigQueryTable) {
          console.log('[EditChartModal] Using dataSourceId object directly:', chart.dataSourceId);
          // Fetch schema for this specific data source
          try {
            const schemaResponse = await apiHelpers.getDataSourceSchema(dsId);
            const schemaData = schemaResponse.data;
            console.log('[EditChartModal] Schema response:', schemaData);
            
            // Try multiple places where columns might be
            const columns = schemaData?.tables?.[0]?.columns || 
                          schemaData?.schema?.tables?.[0]?.columns || 
                          [];
            
            sourceData = {
              id: dsId,
              name: chart.dataSourceId.name,
              tableName: chart.dataSourceId.bigQueryTable,
              schema: columns
            };
            console.log('[EditChartModal] Fetched schema directly:', sourceData.schema?.length, 'columns');
          } catch (schemaError) {
            console.warn('[EditChartModal] Could not fetch schema:', schemaError.message);
          }
        }
        
        name = sourceData?.tableName || sourceData?.name || '';
        console.log('[EditChartModal] Looking for dataSource:', dsId, 'Found:', !!sourceData);
      }
      
      if (sourceData) {
        console.log('[EditChartModal] Found source:', sourceData);
        console.log('[EditChartModal] Schema columns:', sourceData.schema?.length || 0);
        setSchema({
          tables: [{
            name: sourceData.name || name,
            columns: sourceData.schema || []
          }]
        });
        setSourceType(type);
        setTableName(name);
      } else {
        console.warn('[EditChartModal] Source not found - chart may be AI-generated or source deleted');
        // For AI-generated charts or charts with no specific source, try to infer from query
        if (chart.query) {
          // Try to extract table name from query
          const fromMatch = chart.query.match(/FROM\s+[`']?[\w${}]+[`']?\.([`'\w]+)/i);
          if (fromMatch) {
            const inferredTable = fromMatch[1].replace(/[`']/g, '');
            console.log('[EditChartModal] Inferred table from query:', inferredTable);
            setTableName(inferredTable);
            
            // Try to find this table in available sources
            const allSources = [...(sources.dataSources || []), ...(sources.wideViews || []), ...(sources.marts || [])];
            const matchingSource = allSources.find(s => 
              s.tableName === inferredTable || 
              s.dbtModelName === inferredTable ||
              s.name === inferredTable
            );
            
            if (matchingSource && matchingSource.schema?.length > 0) {
              console.log('[EditChartModal] Found source by table name:', matchingSource);
              setSchema({
                tables: [{
                  name: matchingSource.name || inferredTable,
                  columns: matchingSource.schema || []
                }]
              });
              setSourceType(matchingSource.type === 'mart' ? 'mart' : matchingSource.type === 'wideview' ? 'wideview' : 'datasource');
            } else {
              // No matching source or source has no schema - fetch directly from BigQuery
              console.log('[EditChartModal] Fetching schema from BigQuery for table:', inferredTable);
              try {
                // Determine dataset type from table name
                let dataset = 'default';
                if (inferredTable.startsWith('mart_')) dataset = 'marts';
                else if (inferredTable.startsWith('wide_')) dataset = 'wide_views';
                else if (inferredTable.startsWith('stg_')) dataset = 'staging';
                
                const bqResponse = await apiHelpers.getBigQueryTableSchema({ 
                  tableName: inferredTable, 
                  dataset,
                  companyId 
                });
                
                if (bqResponse.data?.columns?.length > 0) {
                  console.log('[EditChartModal] Got schema from BigQuery:', bqResponse.data.columns.length, 'columns');
                  setSchema({
                    tables: [{
                      name: inferredTable,
                      columns: bqResponse.data.columns
                    }]
                  });
                  setSourceType(dataset === 'marts' ? 'mart' : dataset === 'wide_views' ? 'wideview' : 'datasource');
                } else {
                  console.warn('[EditChartModal] No columns found in BigQuery for table:', inferredTable);
                }
              } catch (bqError) {
                console.warn('[EditChartModal] Could not fetch schema from BigQuery:', bqError.message);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('[EditChartModal] Error fetching schema:', error);
    }
  };
  
  const handlePreview = async () => {
    if (!query.trim()) {
      toast.error('Please enter a query first');
      return;
    }
    
    setIsPreviewLoading(true);
    setPreviewError(null);
    
    try {
      const response = await apiHelpers.previewChartQuery({
        chartId: chart._id,
        query: query,
        type: chartType
      });
      
      const rows = response.data.data;
      setPreviewData(rows);

      let frontendOption = getPreviewOption(chartType, rows || [], { config: { ...chart?.chartConfig, multiSeriesLayout } }, { mapLoaded });
      if (frontendOption) {
        const axesTypes = ['pie', 'donut', 'table', 'kpi', 'cohort_table', 'geo', 'funnel'];
        if (!axesTypes.includes(chartType)) {
          if (xAxisLabel.trim() && frontendOption.xAxis && !Array.isArray(frontendOption.xAxis)) {
            frontendOption.xAxis = { ...frontendOption.xAxis, name: xAxisLabel.trim(), nameLocation: 'middle', nameGap: 30, nameTextStyle: { fontWeight: 'bold', fontSize: 12 } };
          }
          if (yAxisLabel.trim() && frontendOption.yAxis && !Array.isArray(frontendOption.yAxis)) {
            frontendOption.yAxis = { ...frontendOption.yAxis, name: yAxisLabel.trim(), nameLocation: 'middle', nameGap: 50, nameRotate: 90, nameTextStyle: { fontWeight: 'bold', fontSize: 12 } };
          }
        }
        if (hideLegend) {
          frontendOption.legend = undefined;
        }
      }
      setPreviewOption(frontendOption || response.data.echartsOption);
    } catch (error) {
      console.error('Preview error:', error);
      setPreviewError(error.response?.data?.error || 'Failed to preview chart. Please check your query syntax.');
    } finally {
      setIsPreviewLoading(false);
    }
  };
  
  const handleSave = async () => {
    if (!chartName.trim()) {
      toast.error('Please enter a chart name');
      return;
    }
    
    if (!query.trim()) {
      toast.error('Please enter a SQL query');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const updatedChartConfig = {
        ...chart.chartConfig,
        xAxis: {
          ...chart.chartConfig?.xAxis,
          label: xAxisLabel.trim() || undefined
        },
        yAxis: {
          ...chart.chartConfig?.yAxis,
          label: yAxisLabel.trim() || undefined
        },
        legend: {
          ...chart.chartConfig?.legend,
          show: !hideLegend,
        },
        multiSeriesLayout: multiSeriesLayout || 'auto',
      };
      if (chartType === 'table') {
        updatedChartConfig.tableConfig = {
          colorRules: tableColorRules.map(r => ({
            id: r.id,
            target: r.target,
            column: r.column || undefined,
            rowIndex: r.rowIndex != null ? r.rowIndex : undefined,
            condition: r.condition?.column ? {
              column: r.condition.column,
              operator: r.condition.operator,
              value: r.condition.value
            } : undefined,
            backgroundColor: r.backgroundColor || undefined,
            textColor: r.textColor || undefined,
          }))
        };
      }
      const updatedChart = await updateChart(chart._id, {
        name: chartName.trim(),
        type: chartType,
        query: query.trim(),
        description: description.trim(),
        chartConfig: updatedChartConfig,
        filterConfig: filterConfig.map(f => ({
          id: f.id,
          column: f.column,
          label: f.label,
          type: f.type,
          columnType: f.columnType,
          enabled: f.enabled,
          defaultValue: f.defaultValue,
          defaultRange: f.defaultRange,
          sourceTable: f.sourceTable,
          sourceColumn: f.sourceColumn
        }))
      });
      
      toast.success('Chart updated successfully!');
      onChartUpdated?.(updatedChart);
      onClose();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.error || 'Failed to update chart');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleClose = () => {
    setActiveTab('query');
    setPreviewData(null);
    setPreviewOption(null);
    setPreviewError(null);
    onClose();
  };
  
  // Handle query generated from visual builder
  const handleVisualQueryGenerated = (generatedQuery, config) => {
    setQuery(generatedQuery);
    setVisualConfig(config);
  };
  
  // AI Assistant handlers
  const handleAiSend = async () => {
    if (!aiInput.trim() || isAiLoading) return;

    const userMessage = aiInput.trim();
    setAiInput('');
    setAiMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsAiLoading(true);
    setAiThinkingStep(0);
    setAiThinkingStartTime(Date.now());
    setAiElapsedTime(0);
    setAiStepDetails({});

    try {
      const companyId = getCurrentCompanyId();

      let dataSourceId = chart.dataSourceId?._id || chart.dataSourceId;
      if (chart.isMart && chart.martId) {
        const martId = chart.martId?._id || chart.martId;
        dataSourceId = `mart_${martId}`;
      }

      const response = await apiHelpers.generateChartWithAIStream(
        {
          message: userMessage,
          companyId,
          dataSourceId,
          currentChartType: chartType,
          existingConfig: chart.chartConfig,
          existingQuery: query,
          existingChartName: chartName,
          isEditMode: true,
          provider: aiProvider,
          model: aiModel
        },
        (progressData) => {
          const step = progressData.step != null ? progressData.step : 0;
          setAiThinkingStep(step);
          if (progressData.details != null) {
            setAiStepDetails(prev => ({ ...prev, [step]: progressData.details }));
          }
        }
      );

      const { response: aiResponse, chartSuggestion, recommendedChartType } = response.data;
      
      // Clean up the response content - remove any raw JSON that might be displayed
      let cleanResponse = aiResponse || '';
      
      // If the response still contains raw JSON, try to clean it up
      if (cleanResponse.includes('"chartSuggestion"') || cleanResponse.includes('"response"')) {
        // Try to extract just the explanation part
        const jsonMatch = cleanResponse.match(/\{[\s\S]*"response"\s*:\s*"([^"]*(?:\\.[^"]*)*)"/);
        if (jsonMatch && jsonMatch[1]) {
          try {
            cleanResponse = JSON.parse(`"${jsonMatch[1]}"`);
          } catch (e) {
            // If parsing fails, try a simpler approach - remove the JSON block
            cleanResponse = cleanResponse
              .replace(/\{[\s\S]*"chartSuggestion"[\s\S]*\}/g, '')
              .replace(/```json[\s\S]*```/g, '')
              .trim();
          }
        }
      }
      
      // If we have a chartSuggestion, add a summary if the response is empty
      if (chartSuggestion && !cleanResponse) {
        cleanResponse = `I've prepared changes for your chart. Click "Apply Changes" to update the query and configuration.`;
      }
      
      // Add AI response to messages
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: cleanResponse || 'I\'ve generated a suggestion for your chart. Click "Apply Changes" to use it.',
        chartSuggestion
      }]);
      
      // Store the suggestion for applying
      if (chartSuggestion) {
        setAiSuggestion({
          ...chartSuggestion,
          recommendedChartType
        });
      }
    } catch (error) {
      console.error('AI chart edit error:', error);
      setAiMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again or rephrase your request.',
        isError: true
      }]);
    } finally {
      setAiThinkingStep(0);
      setAiThinkingStartTime(null);
      setAiElapsedTime(0);
      setAiStepDetails({});
      setIsAiLoading(false);
    }
  };

  const handleAiKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAiSend();
    }
  };
  
  const applyAiSuggestion = () => {
    if (!aiSuggestion) return;
    
    if (aiSuggestion.query) {
      setQuery(aiSuggestion.query);
    }
    if (aiSuggestion.type) {
      setChartType(aiSuggestion.type);
    }
    if (aiSuggestion.name && aiSuggestion.name !== chartName) {
      // Optionally update name if it's different
      setChartName(aiSuggestion.name);
    }
    if (aiSuggestion.config?.xAxis?.label) {
      setXAxisLabel(aiSuggestion.config.xAxis.label);
    }
    if (aiSuggestion.config?.yAxis?.label) {
      setYAxisLabel(aiSuggestion.config.yAxis.label);
    }
    
    setAiSuggestion(null);
    toast.success('AI suggestions applied! Preview to see the changes.');
    setActiveTab('query');
  };
  
  const hasChanges = chart && (
    chartName !== chart.name ||
    chartType !== chart.type ||
    query !== chart.query ||
    description !== (chart.description || '') ||
    xAxisLabel !== (chart.chartConfig?.xAxis?.label || '') ||
    yAxisLabel !== (chart.chartConfig?.yAxis?.label || '') ||
    hideLegend !== (chart.chartConfig?.legend?.show === false) ||
    multiSeriesLayout !== (chart.chartConfig?.multiSeriesLayout || 'auto') ||
    JSON.stringify(filterConfig) !== JSON.stringify(chart.filterConfig || []) ||
    JSON.stringify(tableColorRules) !== JSON.stringify(chart.chartConfig?.tableConfig?.colorRules || [])
  );
  
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden safe-modal-height">
          <div className="flex h-full items-stretch justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden bg-white dark:bg-surface-900 shadow-2xl transition-all flex flex-col h-full">
                {/* Gradient Header */}
                <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-surface-200 dark:border-surface-800 bg-gradient-to-r from-primary-600 to-accent-600">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <PencilIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="min-w-0">
                      <Dialog.Title className="text-base sm:text-lg font-display font-semibold text-white truncate">
                        Edit Chart
                      </Dialog.Title>
                      <p className="text-xs sm:text-sm text-white/70 hidden sm:block">
                        Modify chart settings, query, and configuration
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleClose}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
                  >
                    <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </button>
                </div>

                {/* Mobile toggle */}
                <div className="flex lg:hidden border-b border-surface-200 dark:border-surface-800">
                  <button
                    onClick={() => setMobileTab('preview')}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium text-center transition-colors ${
                      mobileTab === 'preview'
                        ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
                        : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                    }`}
                  >
                    Preview
                  </button>
                  <button
                    onClick={() => setMobileTab('editor')}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium text-center transition-colors ${
                      mobileTab === 'editor'
                        ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
                        : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                    }`}
                  >
                    Editor
                  </button>
                </div>

                {/* Split screen */}
                <div className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0">
                  {/* ====== LEFT PANEL: Chart Type + Preview + Settings + Filters + Save ====== */}
                  <div className={`${mobileTab === 'preview' ? 'flex' : 'hidden'} lg:flex w-full lg:w-1/2 flex-1 flex-col min-h-0 overflow-hidden border-b lg:border-b-0 lg:border-r border-surface-200 dark:border-surface-800`}>
                    {/* Chart Type Selector */}
                    <div className="p-3 sm:p-4 border-b border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/50 flex-shrink-0">
                      <label className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-2 block">
                        Chart Type
                      </label>
                      <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                        {CHART_TYPES.map((type) => {
                          const Icon = type.icon;
                          const isSelected = type.id === chartType;
                          return (
                            <button
                              key={type.id}
                              onClick={() => setChartType(type.id)}
                              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border-2 transition-all text-xs sm:text-sm ${
                                isSelected
                                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                                  : 'border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-400 hover:border-surface-400'
                              }`}
                              title={type.description}
                            >
                              <Icon className="w-4 h-4" />
                              {type.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Preview Area */}
                    <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                      <div className="flex items-center justify-between px-3 sm:px-4 pt-3 sm:pt-4 pb-2 flex-shrink-0">
                        <h3 className="font-medium text-surface-900 dark:text-white text-sm">Preview</h3>
                        <button
                          onClick={handlePreview}
                          disabled={isPreviewLoading || !query.trim()}
                          className="btn-ghost btn-sm text-xs"
                        >
                          {isPreviewLoading ? (
                            <><ArrowPathIcon className="w-3.5 h-3.5 animate-spin mr-1" />Refreshing...</>
                          ) : (
                            <><ArrowPathIcon className="w-3.5 h-3.5 mr-1" />Refresh</>
                          )}
                        </button>
                      </div>
                      <div className="flex-1 min-h-0 mx-3 sm:mx-4 mb-3 sm:mb-4 rounded-xl border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800/50 overflow-hidden">
                        {isPreviewLoading ? (
                          <div className="h-full flex items-center justify-center">
                            <div className="text-center">
                              <div className="w-10 h-10 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin mx-auto mb-3" />
                              <p className="text-sm text-surface-500">Running query...</p>
                            </div>
                          </div>
                        ) : previewError ? (
                          <div className="h-full flex items-center justify-center">
                            <div className="text-center max-w-md p-4">
                              <div className="p-3 bg-red-50 dark:bg-red-950/30 rounded-lg mb-3">
                                <p className="text-sm text-red-700 dark:text-red-300">{previewError}</p>
                              </div>
                              <button onClick={handlePreview} disabled={!query.trim()} className="btn-secondary text-sm">
                                Retry Preview
                              </button>
                            </div>
                          </div>
                        ) : !previewData ? (
                          <div className="h-full flex items-center justify-center">
                            <div className="text-center p-4">
                              <EyeIcon className="w-10 h-10 text-surface-300 dark:text-surface-600 mx-auto mb-3" />
                              <p className="text-sm text-surface-500 mb-3">No preview yet</p>
                              <button onClick={handlePreview} disabled={!query.trim()} className="btn-primary text-sm">
                                Generate Preview
                              </button>
                            </div>
                          </div>
                        ) : chartType === 'table' ? (
                          <div className="h-full overflow-auto">
                            {(() => {
                              const cols = previewData?.[0] ? Object.keys(previewData[0]) : [];
                              return (
                                <table className="w-full text-sm">
                                  <thead className="bg-surface-100 dark:bg-surface-700 sticky top-0">
                                    <tr>
                                      {cols.map(key => {
                                        const hStyle = getTableHeaderStyle(tableColorRules, key);
                                        return (
                                          <th key={key} className="px-4 py-2 text-left font-medium text-surface-600 dark:text-surface-400 border-b border-surface-200 dark:border-surface-700" style={hStyle || undefined}>
                                            {key}
                                          </th>
                                        );
                                      })}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {previewData?.slice(0, 50).map((row, i) => (
                                      <tr key={i} className="border-b border-surface-100 dark:border-surface-800">
                                        {cols.map((col, j) => {
                                          const cellStyle = getTableCellStyle(tableColorRules, row, i, col);
                                          return (
                                            <td key={j} className="px-4 py-2 text-surface-700 dark:text-surface-300" style={cellStyle || undefined}>
                                              {String(row[col] ?? '—')}
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              );
                            })()}
                          </div>
                        ) : chartType === 'kpi' ? (
                          <div className="h-full flex items-center justify-center">
                            <div className="text-center">
                              <div className="text-4xl font-display font-bold text-surface-900 dark:text-white">
                                {previewData?.[0] ? Object.values(previewData[0])[0] : '—'}
                              </div>
                              <p className="text-surface-500 mt-2 text-sm">
                                {previewData?.[0] ? Object.keys(previewData[0])[0] : 'Value'}
                              </p>
                            </div>
                          </div>
                        ) : chartType === 'cohort_table' && previewData?.length > 0 ? (
                          <div className="h-full overflow-auto">
                            {(() => {
                              const keys = Object.keys(previewData[0] || {});
                              const cohortField =
                                keys.find(k =>
                                  k.toLowerCase().includes('cohort') ||
                                  (k.toLowerCase().includes('month') && !k.toLowerCase().includes('period'))
                                ) || keys[0];
                              const periodField =
                                keys.find(k =>
                                  k.toLowerCase().includes('period') ||
                                  k.toLowerCase().includes('age') ||
                                  k.toLowerCase() === 'm'
                                ) || keys[1];
                              const valueField =
                                keys.find(k =>
                                  ['value', 'users', 'count', 'retention', 'rate'].some(t =>
                                    k.toLowerCase().includes(t)
                                  )
                                ) || keys[2] || keys[1];

                              const cohorts = [...new Set(previewData.map(row => row[cohortField]))].sort();
                              const periods = [...new Set(previewData.map(row => Number(row[periodField])))].sort(
                                (a, b) => a - b
                              );

                              const pivotMap = {};
                              previewData.forEach(row => {
                                const c = row[cohortField];
                                const p = Number(row[periodField]);
                                if (!pivotMap[c]) pivotMap[c] = {};
                                pivotMap[c][p] = row[valueField];
                              });

                              const getCellStyle = (value, cohort) => {
                                if (value === undefined || value === null) return {};
                                const baseValue = pivotMap[cohort]?.[0] || 1;
                                const intensity = baseValue > 0 ? Math.min(value / baseValue, 1) : 0;
                                return {
                                  backgroundColor: `rgba(99, 102, 241, ${intensity * 0.5})`,
                                  color: intensity > 0.6 ? 'white' : 'inherit',
                                };
                              };

                              return (
                                <table className="w-full text-sm border-collapse">
                                  <thead className="bg-surface-100 dark:bg-surface-700 sticky top-0">
                                    <tr>
                                      <th className="px-3 py-2 text-left font-semibold text-surface-700 dark:text-surface-300 border-b border-r border-surface-200 dark:border-surface-600 bg-surface-100 dark:bg-surface-700">
                                        Cohort
                                      </th>
                                      {periods.map(period => (
                                        <th
                                          key={period}
                                          className="px-3 py-2 text-center font-semibold text-surface-700 dark:text-surface-300 border-b border-surface-200 dark:border-surface-600 bg-surface-100 dark:bg-surface-700 min-w-[50px]"
                                        >
                                          M{period}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {cohorts.map(cohort => (
                                      <tr key={cohort} className="border-b border-surface-100 dark:border-surface-700">
                                        <td className="px-3 py-2 font-medium text-surface-900 dark:text-white border-r border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800/50 whitespace-nowrap">
                                          {formatCellValue(cohort)}
                                        </td>
                                        {periods.map(period => {
                                          const value = pivotMap[cohort]?.[period];
                                          const cellStyle = getCellStyle(value, cohort);
                                          return (
                                            <td key={period} className="px-3 py-2 text-center" style={cellStyle}>
                                              {value !== undefined
                                                ? typeof value === 'number'
                                                  ? value.toLocaleString()
                                                  : formatCellValue(value)
                                                : '—'}
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              );
                            })()}
                          </div>
                        ) : chartType === 'geo' && previewData?.length > 0 && !mapLoaded ? (
                          <div className="h-full flex flex-col items-center justify-center text-center p-8">
                            <div className="w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin mb-3" />
                            <p className="text-sm text-surface-500">Loading map data...</p>
                          </div>
                        ) : previewOption ? (
                          <div className="h-full p-3">
                            <ReactECharts
                              option={previewOption}
                              notMerge={true}
                              style={{ height: '100%', width: '100%' }}
                              opts={{ renderer: 'svg' }}
                            />
                          </div>
                        ) : (
                          <div className="h-full flex items-center justify-center text-sm text-surface-500">
                            Unable to generate chart preview
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Collapsible: Chart Settings */}
                    <div className="border-t border-surface-200 dark:border-surface-800 flex-shrink-0">
                      <button
                        onClick={() => setShowSettings(!showSettings)}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-surface-50 dark:hover:bg-surface-800/50 transition-colors text-left"
                      >
                        <div className="flex items-center gap-2">
                          <AdjustmentsHorizontalIcon className="w-4 h-4 text-surface-500 dark:text-surface-400" />
                          <span className="text-sm font-medium text-surface-700 dark:text-surface-300">
                            Chart Settings
                          </span>
                          {(chartName.trim() || xAxisLabel.trim() || yAxisLabel.trim()) && (
                            <span className="px-1.5 py-0.5 text-xs bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-full">
                              {chartName.trim() ? chartName.trim() : 'Configured'}
                            </span>
                          )}
                        </div>
                        <ChevronDownIcon className={`w-4 h-4 text-surface-400 transition-transform ${showSettings ? 'rotate-180' : ''}`} />
                      </button>

                      {showSettings && (
                        <div className="px-4 py-3 bg-surface-50 dark:bg-surface-900/50 space-y-3 max-h-60 overflow-y-auto">
                          <div>
                            <label className="text-xs font-medium text-surface-600 dark:text-surface-400 mb-1 block">Chart Name</label>
                            <input
                              type="text"
                              value={chartName}
                              onChange={(e) => setChartName(e.target.value)}
                              placeholder="Enter chart name"
                              className="input w-full"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-surface-600 dark:text-surface-400 mb-1 block">Description (optional)</label>
                            <input
                              type="text"
                              value={description}
                              onChange={(e) => setDescription(e.target.value)}
                              placeholder="Brief description"
                              className="input w-full"
                            />
                          </div>
                          {hasAxes && (
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs font-medium text-surface-600 dark:text-surface-400 mb-1 block">X-Axis Label</label>
                                <input
                                  type="text"
                                  value={xAxisLabel}
                                  onChange={(e) => setXAxisLabel(e.target.value)}
                                  placeholder="Auto if empty"
                                  className="input w-full"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-medium text-surface-600 dark:text-surface-400 mb-1 block">Y-Axis Label</label>
                                <input
                                  type="text"
                                  value={yAxisLabel}
                                  onChange={(e) => setYAxisLabel(e.target.value)}
                                  placeholder="Auto if empty"
                                  className="input w-full"
                                />
                              </div>
                            </div>
                          )}
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={hideLegend}
                              onChange={(e) => setHideLegend(e.target.checked)}
                              className="rounded border-surface-300 dark:border-surface-600 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-xs font-medium text-surface-600 dark:text-surface-400">Hide legend (series key)</span>
                          </label>
                          {hasAxes && (
                            <div className="mt-2">
                              <label className="text-xs font-medium text-surface-600 dark:text-surface-400 mb-1 block">
                                Multi-Series Layout
                              </label>
                              <select
                                value={multiSeriesLayout}
                                onChange={(e) => setMultiSeriesLayout(e.target.value)}
                                className="input w-full"
                              >
                                <option value="auto">Auto (dual-axis when scales differ)</option>
                                <option value="shared_axis">Shared Axis (grouped bars)</option>
                                <option value="dual_axis">Dual Axis (bar + line combo)</option>
                              </select>
                              <p className="text-xs text-surface-400 mt-1">
                                Controls how multiple numeric series are displayed together.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Collapsible: Filters */}
                    <div className="border-t border-surface-200 dark:border-surface-800 flex-shrink-0">
                      <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-surface-50 dark:hover:bg-surface-800/50 transition-colors text-left"
                      >
                        <div className="flex items-center gap-2">
                          <FunnelIcon className="w-4 h-4 text-surface-500 dark:text-surface-400" />
                          <span className="text-sm font-medium text-surface-700 dark:text-surface-300">
                            Configure Filters
                          </span>
                          {filterConfig.length > 0 && (
                            <span className="px-1.5 py-0.5 text-xs bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-full">
                              {filterConfig.filter(f => f.enabled).length}
                            </span>
                          )}
                        </div>
                        <ChevronDownIcon className={`w-4 h-4 text-surface-400 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                      </button>

                      {showFilters && (
                        <div className="px-4 py-3 bg-surface-50 dark:bg-surface-900/50 max-h-60 overflow-y-auto">
                          <FilterConfigurator
                            query={query}
                            schema={schema}
                            filterConfig={filterConfig}
                            onFilterConfigChange={setFilterConfig}
                          />
                        </div>
                      )}
                    </div>

                    {/* Collapsible: Table Styling (only for table charts) */}
                    {chartType === 'table' && (
                      <div className="border-t border-surface-200 dark:border-surface-800 flex-shrink-0">
                        <button
                          onClick={() => setShowTableStyling(!showTableStyling)}
                          className="w-full px-4 py-3 flex items-center justify-between hover:bg-surface-50 dark:hover:bg-surface-800/50 transition-colors text-left"
                        >
                          <div className="flex items-center gap-2">
                            <SwatchIcon className="w-4 h-4 text-surface-500 dark:text-surface-400" />
                            <span className="text-sm font-medium text-surface-700 dark:text-surface-300">
                              Table Colors
                            </span>
                            {tableColorRules.length > 0 && (
                              <span className="px-1.5 py-0.5 text-xs bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-full">
                                {tableColorRules.length}
                              </span>
                            )}
                          </div>
                          <ChevronDownIcon className={`w-4 h-4 text-surface-400 transition-transform ${showTableStyling ? 'rotate-180' : ''}`} />
                        </button>

                        {showTableStyling && (
                          <div className="px-4 py-3 bg-surface-50 dark:bg-surface-900/50 max-h-72 overflow-y-auto">
                            <TableColorConfigurator
                              colorRules={tableColorRules}
                              columns={previewData?.[0] ? Object.keys(previewData[0]) : []}
                              onChange={setTableColorRules}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Save Bar */}
                    <div className="p-3 sm:p-4 border-t border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/50 flex-shrink-0">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-surface-500">
                          {hasChanges ? (
                            <span className="text-amber-600 dark:text-amber-400">Unsaved changes</span>
                          ) : (
                            <span>No changes</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={handleClose} className="btn-secondary btn-sm">
                            Cancel
                          </button>
                          <button
                            onClick={handleSave}
                            disabled={isLoading || !hasChanges || !chartName.trim() || !query.trim()}
                            className="btn-primary btn-sm"
                          >
                            {isLoading ? (
                              <><div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-1.5" />Saving...</>
                            ) : (
                              'Save Changes'
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ====== RIGHT PANEL: Data Source + Tabs (Query, AI) ====== */}
                  <div className={`${mobileTab === 'editor' ? 'flex' : 'hidden'} lg:flex w-full lg:w-1/2 flex-1 flex-col min-h-0 overflow-hidden`}>
                    {/* Data Source (read-only) */}
                    <div className="flex-shrink-0 p-4 border-b border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/50">
                      <label className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-1 block">
                        Data Source
                      </label>
                      <div className="p-3 bg-white dark:bg-surface-800 rounded-lg border border-surface-200 dark:border-surface-700">
                        <p className="text-sm text-surface-700 dark:text-surface-300">
                          {chart?.isMart && chart?.martId ? (
                            <span className="flex items-center gap-1">
                              <span className="text-purple-600 dark:text-purple-400 font-medium">Mart:</span>
                              {chart.martId.displayName || chart.martId.name || chart.dbtModelName}
                            </span>
                          ) : chart?.isWideView && chart?.wideViewName ? (
                            <span className="flex items-center gap-1">
                              <span className="text-blue-600 dark:text-blue-400 font-medium">View:</span>
                              {chart.wideViewName}
                            </span>
                          ) : chart?.dataSourceId?.name || chart?.dataSource?.name || (chart?.aiGenerated ? 'AI Generated (Direct Query)' : 'Unknown')}
                        </p>
                      </div>
                    </div>

                    {/* Tab bar */}
                    <div className="flex-shrink-0 flex border-b border-surface-200 dark:border-surface-800">
                      <button
                        onClick={() => setActiveTab('query')}
                        className={`flex-1 px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                          activeTab === 'query'
                            ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
                            : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                        }`}
                      >
                        <CodeBracketIcon className="w-4 h-4" />
                        SQL Query
                      </button>
                      <button
                        onClick={() => setActiveTab('ai')}
                        className={`flex-1 px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                          activeTab === 'ai'
                            ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
                            : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                        }`}
                      >
                        <SparklesIcon className="w-4 h-4" />
                        AI Assistant
                        {formatCost('chart_builder') && (
                          <span className="text-xs opacity-60 hidden sm:inline">{formatCost('chart_builder')}</span>
                        )}
                      </button>
                    </div>

                    {/* Tab Content */}
                    <div className="flex-1 min-h-0 overflow-hidden">
                      {/* Query Tab */}
                      {activeTab === 'query' && (
                        <div className="h-full overflow-y-auto p-4 space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 bg-surface-100 dark:bg-surface-800 p-1 rounded-lg">
                              <button
                                onClick={() => setMode('visual')}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                                  mode === 'visual'
                                    ? 'bg-white dark:bg-surface-700 text-primary-600 dark:text-primary-400 shadow-sm'
                                    : 'text-surface-600 dark:text-surface-400 hover:text-surface-800 dark:hover:text-surface-200'
                                }`}
                              >
                                <CubeIcon className="w-4 h-4" />
                                Visual Builder
                              </button>
                              <button
                                onClick={() => setMode('sql')}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                                  mode === 'sql'
                                    ? 'bg-white dark:bg-surface-700 text-primary-600 dark:text-primary-400 shadow-sm'
                                    : 'text-surface-600 dark:text-surface-400 hover:text-surface-800 dark:hover:text-surface-200'
                                }`}
                              >
                                <CodeBracketIcon className="w-4 h-4" />
                                SQL Editor
                              </button>
                            </div>
                            <button
                              onClick={handlePreview}
                              disabled={isPreviewLoading || !query.trim()}
                              className="btn-secondary text-sm py-1.5 px-3"
                            >
                              {isPreviewLoading ? (
                                <><ArrowPathIcon className="w-4 h-4 mr-1.5 animate-spin" />Loading...</>
                              ) : (
                                <><EyeIcon className="w-4 h-4 mr-1.5" />Preview</>
                              )}
                            </button>
                          </div>

                          {mode === 'visual' ? (
                            <div className="space-y-4">
                              <VisualChartBuilder
                                schema={schema}
                                chartType={chartType}
                                sourceType={sourceType}
                                tableName={tableName}
                                onQueryGenerated={handleVisualQueryGenerated}
                                initialConfig={visualConfig}
                              />
                              {query && (
                                <div className="bg-surface-50 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-medium text-surface-500 uppercase tracking-wide">Generated SQL</span>
                                    <button onClick={() => setMode('sql')} className="text-xs text-primary-600 dark:text-primary-400 hover:underline">
                                      Edit directly
                                    </button>
                                  </div>
                                  <pre className="text-xs font-mono text-surface-700 dark:text-surface-300 whitespace-pre-wrap overflow-x-auto">
                                    {query}
                                  </pre>
                                </div>
                              )}
                            </div>
                          ) : (
                            <>
                              <textarea
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="SELECT * FROM ${dataset}.table_name"
                                rows={12}
                                className="input font-mono text-sm w-full"
                                style={{ minHeight: '300px' }}
                              />
                              <p className="text-xs text-surface-500">
                                Use <code className="bg-surface-100 dark:bg-surface-800 px-1 py-0.5 rounded">{'${dataset}'}</code> as the dataset placeholder.
                              </p>
                            </>
                          )}

                          {previewError && (
                            <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
                              <p className="text-sm text-red-700 dark:text-red-300">{previewError}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* AI Assistant Tab */}
                      {activeTab === 'ai' && (
                        <div className="h-full flex flex-col">
                          <div className="hidden">
                            <select value={aiProvider} onChange={(e) => setAiProvider(e.target.value)}>
                              {Object.entries(aiProviders).map(([key, value]) => (
                                <option key={key} value={key}>{value.name}</option>
                              ))}
                            </select>
                            <select value={aiModel} onChange={(e) => setAiModel(e.target.value)}>
                              {aiModels.filter(m => m.provider === aiProvider).map(m => (
                                <option key={m.id} value={m.id}>{m.name}</option>
                              ))}
                            </select>
                          </div>

                          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-surface-50 dark:bg-surface-800/30">
                            {aiMessages.length === 0 ? (
                              <div className="h-full flex flex-col items-center justify-center text-center">
                                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mb-4">
                                  <SparklesIcon className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-lg font-display font-bold text-surface-900 dark:text-white mb-2">
                                  How can I help you edit this chart?
                                </h3>
                                <p className="text-surface-500 max-w-sm mb-6">
                                  Describe the changes you want to make, and I'll update the SQL query and configuration.
                                </p>
                                <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                                  {[
                                    'Change to a line chart',
                                    'Add a filter for last 30 days',
                                    'Group by month instead',
                                    'Show top 10 only',
                                    'Add a comparison to previous period'
                                  ].map((suggestion, i) => (
                                    <button
                                      key={i}
                                      onClick={() => setAiInput(suggestion)}
                                      className="px-3 py-2 rounded-full bg-white dark:bg-surface-700 text-sm text-surface-600 dark:text-surface-400 hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/30 dark:hover:text-primary-400 transition-colors border border-surface-200 dark:border-surface-600"
                                    >
                                      {suggestion}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <>
                                {aiMessages.map((msg, i) => (
                                  <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                      msg.role === 'assistant'
                                        ? 'bg-gradient-to-br from-primary-500 to-accent-500'
                                        : 'bg-surface-200 dark:bg-surface-700'
                                    }`}>
                                      {msg.role === 'assistant'
                                        ? <SparklesIcon className="w-4 h-4 text-white" />
                                        : <UserIcon className="w-4 h-4 text-surface-600 dark:text-surface-400" />
                                      }
                                    </div>
                                    <div className={`max-w-[85%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                                      <div className={`inline-block px-4 py-3 rounded-2xl ${
                                        msg.role === 'assistant'
                                          ? msg.isError
                                            ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                            : 'bg-white dark:bg-surface-800 text-surface-900 dark:text-white shadow-sm'
                                          : 'bg-primary-600 text-white'
                                      }`}>
                                        {msg.role === 'assistant' ? (
                                          <div className="prose prose-sm dark:prose-invert max-w-none">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                                          </div>
                                        ) : (
                                          <p className="whitespace-pre-wrap">{msg.content}</p>
                                        )}
                                      </div>
                                      {msg.role === 'assistant' && msg.chartSuggestion && (
                                        <div className="mt-2">
                                          <button
                                            onClick={() => {
                                              const suggestion = msg.chartSuggestion;
                                              if (suggestion.query) setQuery(suggestion.query);
                                              if (suggestion.type) setChartType(suggestion.type);
                                              if (suggestion.name) setChartName(suggestion.name);
                                              if (suggestion.config?.xAxis?.label) setXAxisLabel(suggestion.config.xAxis.label);
                                              if (suggestion.config?.yAxis?.label) setYAxisLabel(suggestion.config.yAxis.label);
                                              toast.success('AI suggestions applied! Click Refresh to preview.');
                                              setActiveTab('query');
                                            }}
                                            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
                                          >
                                            <LightBulbIcon className="w-4 h-4" />
                                            Apply Changes
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                {isAiLoading && (
                                  <ThinkingChain
                                    currentStep={Math.min(aiThinkingStep, 4)}
                                    elapsedTime={aiElapsedTime}
                                    stepDetails={aiStepDetails}
                                  />
                                )}
                              </>
                            )}
                            <div ref={aiMessagesEndRef} />
                          </div>

                          {aiSuggestion && (
                            <div className="mx-4 mt-3 p-3 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-lg flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <LightBulbIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                                <span className="text-sm text-primary-700 dark:text-primary-300">AI suggestion ready</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <button onClick={() => setAiSuggestion(null)} className="px-3 py-1 text-sm text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-md transition-colors">
                                  Dismiss
                                </button>
                                <button onClick={applyAiSuggestion} className="px-3 py-1 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors">
                                  Apply
                                </button>
                              </div>
                            </div>
                          )}

                          <div className="p-4 flex gap-3 flex-shrink-0">
                            <textarea
                              value={aiInput}
                              onChange={(e) => setAiInput(e.target.value)}
                              onKeyDown={handleAiKeyDown}
                              placeholder="Describe changes to your chart..."
                              rows={2}
                              className="flex-1 input resize-none"
                            />
                            <button
                              onClick={handleAiSend}
                              disabled={!aiInput.trim() || isAiLoading}
                              className="btn-primary self-end px-4"
                              title={`Send ${formatCost('chart_builder')}`}
                            >
                              <PaperAirplaneIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

